import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for audit_log */
export type AuditLogId = string & { __flavor: "AuditLogId" };

/**
 * Represents the table public.audit_log
 * @uniqueConstraints {"audit_log_pkey":{"name":"audit_log_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"ix_audit_log_table_name":{"name":"ix_audit_log_table_name","isPrimary":false,"isUnique":false,"columns":[{"name":"table_name","definition":"table_name","predicate":null}]},"ix_audit_log_record_id":{"name":"ix_audit_log_record_id","isPrimary":false,"isUnique":false,"columns":[{"name":"record_id","definition":"record_id","predicate":null}]},"ix_audit_log_operation_type":{"name":"ix_audit_log_operation_type","isPrimary":false,"isUnique":false,"columns":[{"name":"operation_type","definition":"operation_type","predicate":null}]},"ix_audit_log_changed_by":{"name":"ix_audit_log_changed_by","isPrimary":false,"isUnique":false,"columns":[{"name":"changed_by","definition":"changed_by","predicate":null}]},"ix_audit_log_transaction_id":{"name":"ix_audit_log_transaction_id","isPrimary":false,"isUnique":false,"columns":[{"name":"transaction_id","definition":"transaction_id","predicate":null}]},"ix_audit_log_url":{"name":"ix_audit_log_url","isPrimary":false,"isUnique":false,"columns":[{"name":"url","definition":"url","predicate":null}]},"ix_audit_log_ip":{"name":"ix_audit_log_ip","isPrimary":false,"isUnique":false,"columns":[{"name":"ip","definition":"ip","predicate":null}]},"ix_audit_log_created_at":{"name":"ix_audit_log_created_at","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]}}
 */
export default interface AuditLogTable {
  id: ColumnType<AuditLogId, AuditLogId | undefined, AuditLogId>;

  tableName: ColumnType<string, string, string>;

  recordId: ColumnType<string, string, string>;

  operationType: ColumnType<string, string, string>;

  changedBy: ColumnType<string, string, string>;

  oldData: ColumnType<unknown | null, unknown | null, unknown | null>;

  newData: ColumnType<unknown | null, unknown | null, unknown | null>;

  impersonator: ColumnType<string | null, string | null, string | null>;

  transactionId: ColumnType<string, string, string>;

  url: ColumnType<string | null, string | null, string | null>;

  ip: ColumnType<string | null, string | null, string | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;
}

export type AuditLog = Selectable<AuditLogTable>;

export type NewAuditLog = Insertable<AuditLogTable>;

export type AuditLogUpdate = Updateable<AuditLogTable>;

export const auditLogId = z.number() as unknown as z.Schema<AuditLogId>;

export const auditLog = z.object({
  id: auditLogId,
  tableName: z.string(),
  recordId: z.string(),
  operationType: z.string(),
  changedBy: z.string(),
  oldData: z.unknown().nullable(),
  newData: z.unknown().nullable(),
  impersonator: z.string().nullable(),
  transactionId: z.string(),
  url: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.date(),
});

export const auditLogInitializer = z.object({
  id: auditLogId.optional(),
  tableName: z.string(),
  recordId: z.string(),
  operationType: z.string(),
  changedBy: z.string(),
  oldData: z.unknown().optional().nullable(),
  newData: z.unknown().optional().nullable(),
  impersonator: z.string().optional().nullable(),
  transactionId: z.string(),
  url: z.string().optional().nullable(),
  ip: z.string().optional().nullable(),
  createdAt: z.date().optional(),
});

export const auditLogMutator = z.object({
  id: auditLogId.optional(),
  tableName: z.string().optional(),
  recordId: z.string().optional(),
  operationType: z.string().optional(),
  changedBy: z.string().optional(),
  oldData: z.unknown().optional().nullable(),
  newData: z.unknown().optional().nullable(),
  impersonator: z.string().optional().nullable(),
  transactionId: z.string().optional(),
  url: z.string().optional().nullable(),
  ip: z.string().optional().nullable(),
  createdAt: z.date().optional(),
});

// ── constraints for AuditLog ─────────────────────────────────
export const AuditLogConstraints = {
  audit_log_pkey: {
    name: "audit_log_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  ix_audit_log_table_name: {
    name: "ix_audit_log_table_name",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "table_name", definition: "table_name", predicate: null }],
  },
  ix_audit_log_record_id: {
    name: "ix_audit_log_record_id",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "record_id", definition: "record_id", predicate: null }],
  },
  ix_audit_log_operation_type: {
    name: "ix_audit_log_operation_type",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "operation_type", definition: "operation_type", predicate: null }],
  },
  ix_audit_log_changed_by: {
    name: "ix_audit_log_changed_by",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "changed_by", definition: "changed_by", predicate: null }],
  },
  ix_audit_log_transaction_id: {
    name: "ix_audit_log_transaction_id",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "transaction_id", definition: "transaction_id", predicate: null }],
  },
  ix_audit_log_url: {
    name: "ix_audit_log_url",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "url", definition: "url", predicate: null }],
  },
  ix_audit_log_ip: {
    name: "ix_audit_log_ip",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "ip", definition: "ip", predicate: null }],
  },
  ix_audit_log_created_at: {
    name: "ix_audit_log_created_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
} as const;
export type AuditLogConstraintsType = typeof AuditLogConstraints;
